import React from 'react';
import { Link } from 'gatsby';
import Button from '../components/Button';
import Seo from '../components/Seo';
import Layout from '../components/layout/Layout';
import PricingImage from '../svg/PricingImage';
import CallToAction from '../components/CallToAction';
import Card from '../components/Card';
import CheckIcon from '../components/CheckIcon';
import CustomerCard from '../components/home/CustomerCard';
import GoogleReviewBadge from '../components/GoogleReviewBadge';
import customerData from '../data/customer-data';

export default () => (
  <Layout>
    <Seo title="Pricing" page="pricing" />
    <section className="pt-20 md:pt-40">
      <div className="container mx-auto px-8 lg:flex">
        <div className="lg:w-1/2">
          <PricingImage />
        </div>
        <div className="text-center lg:text-right lg:w-1/2">
          <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold leading-none">Pricing</h1>
          <p className="text-xl lg:text-2xl mt-6 font-light">
            Transparent pricing for your business needs
          </p>
          <p className="mt-8 md:mt-12">
            <Link to="/contact">
              <Button size="lg">Contact Us</Button>
            </Link>
          </p>
          <p className="mt-4 text-gray-600">Let us know how we can help you</p>
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="container mx-auto text-center">
        <h2 className="text-3xl lg:text-5xl font-semibold mb-16">Bookkeeping Services</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 px-8 max-w-6xl mx-auto">
          <Card className="border-2 border-tertiary">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4 text-tertiary">Simple</h3>
              <div className="text-4xl font-bold mb-6">
                $295<span className="text-lg text-gray-600">/month</span>
              </div>
              <ul className="text-left space-y-3 mb-8">
                <li className="flex items-center">
                  <CheckIcon />
                  Weekly posting bank and credit card transactions (up to 250 per month)
                </li>
                <li className="flex items-center">
                  <CheckIcon />
                  Monthly reconciliations
                </li>
                <li className="flex items-center">
                  <CheckIcon />
                  Monthly Balance Sheet and Profit & Loss
                </li>
                <li className="flex items-center">
                  <CheckIcon />
                  Call, text and email support
                </li>
              </ul>
            </div>
          </Card>

          <Card className="border-2 border-primary bg-green-50">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4 text-primary">Essentials</h3>
              <div className="text-4xl font-bold mb-6">
                $495<span className="text-lg text-gray-600">/month</span>
              </div>
              <ul className="text-left space-y-3 mb-8">
                <li className="flex items-center">
                  <CheckIcon />
                  Everything in Simple plan
                </li>
                <li className="flex items-center">
                  <CheckIcon />
                  Weekly posting bank and credit card transactions (up to 500 per month)
                </li>
                <li className="flex items-center">
                  <CheckIcon />
                  Annual business registration renewal
                </li>
              </ul>
            </div>
          </Card>

          <Card className="border-2 border-secondary">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4 text-secondary">Pro</h3>
              <div className="text-4xl font-bold mb-6">
                $695<span className="text-lg text-gray-600">/month</span>
              </div>
              <ul className="text-left space-y-3 mb-8">
                <li className="flex items-center">
                  <CheckIcon />
                  Everything in Essentials plan
                </li>
                <li className="flex items-center">
                  <CheckIcon />
                  Weekly posting bank and credit card transactions (up to 1,000 per month)
                </li>
                <li className="flex items-center">
                  <CheckIcon />
                  Year-end tax planning
                </li>
              </ul>
            </div>
          </Card>
        </div>
      </div>
    </section>

    <section className="py-16 bg-primary text-white">
      <div className="container mx-auto text-center px-8">
        <h2 className="text-3xl lg:text-5xl font-semibold mb-12">Custom Pricing</h2>
        <div className="max-w-3xl mx-auto">
          <ul className="text-left space-y-4 text-lg">
            <li className="flex items-start">
              <span className="font-semibold">
                All plans are customizable to meet your unique bookkeeping and tax needs.
              </span>
            </li>
            <li className="flex items-start">
              <div>
                <span className="font-semibold">Additional available services</span>
                <ul className="mt-3 space-y-2 ml-6">
                  <li className="flex items-start">
                    <span className="mr-3">•</span>
                    <span>KPI tracking</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3">•</span>
                    <span>Sales tax filings</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3">•</span>
                    <span>Payroll support</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3">•</span>
                    <span>Business registration services</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3">•</span>
                    <span>Catch up bookkeeping</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-3">•</span>
                    <span>Discounted QuickBooks subscription</span>
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section className="py-16 bg-gray-50">
      <div className="container mx-auto text-center">
        <h2 className="text-3xl lg:text-5xl font-semibold mb-16">Tax Services</h2>
        <div className="max-w-2xl mx-auto px-8">
          <Card className="border-2 border-primary-lighter">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary">
                $495<span className="text-lg text-gray-600">/month</span>
              </div>
              <p className="text-lg mb-6 text-gray-700">Starting price</p>
              <ul className="text-left space-y-2 max-w-md mx-auto">
                <li className="flex items-center">
                  <CheckIcon className="w-4 h-4 text-primary mr-3 flex-shrink-0" />
                  S-Corp
                </li>
                <li className="flex items-center">
                  <CheckIcon className="w-4 h-4 text-primary mr-3 flex-shrink-0" />
                  C-Corp
                </li>
                <li className="flex items-center">
                  <CheckIcon className="w-4 h-4 text-primary mr-3 flex-shrink-0" />
                  Partnership
                </li>
                <li className="flex items-center">
                  <CheckIcon className="w-4 h-4 text-primary mr-3 flex-shrink-0" />
                  Individual with a business on Schedule C
                </li>
                <li className="flex items-center">
                  <CheckIcon className="w-4 h-4 text-primary mr-3 flex-shrink-0" />
                  Individual with Rental Property
                </li>
              </ul>
            </div>
          </Card>
        </div>
      </div>
    </section>

    <section className="py-16">
      <div className="container mx-auto text-center">
        <h2 className="text-3xl lg:text-5xl font-semibold mb-16">Specialized Services</h2>
        <div className="grid md:grid-cols-2 gap-8 px-8 max-w-4xl mx-auto">
          <Card className="border-2 border-primary">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4 text-primary">Cost Segregation</h3>
              <div className="text-4xl font-bold mb-6 text-primary">$495</div>
              <div className="border-t pt-4">
                <p className="text-gray-700">
                  * An additional <span className="font-bold">$395</span> will be charged if Form
                  3115 &quot;Application for Change in Accounting Method&quot; is required.
                </p>
              </div>
            </div>
          </Card>

          <Card className="border-2 border-primary">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4 text-primary">R&D Tax Credit Calculation</h3>
              <div className="text-4xl font-bold mb-6 text-primary">$1,495</div>
            </div>
          </Card>
        </div>
      </div>
    </section>

    <section id="testimonials" className="py-20 lg:pb-20 lg:pt-20">
      <div className="bg-primary py-12">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl lg:text-5xl font-semibold text-white">Testimonials</h2>
          <p className="text-xl lg:text-2xl mt-6 font-light text-white">
            What customers are saying
          </p>
        </div>
      </div>
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row md:-mx-3 mt-12">
          {customerData.slice(3).map(customer => (
            <div key={customer.customerName} className="flex flex-1 px-3">
              <CustomerCard customer={customer} />
            </div>
          ))}
        </div>
      </div>
    </section>

    <CallToAction />
    <GoogleReviewBadge className="fixed max-w-none left-0 bottom-0 mb-8 ml-8 hidden md:block" />
  </Layout>
);
